import { useState, useCallback, useRef, useEffect } from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { springs } from "@/components/ui/motion";
import { SideSheet } from "@/components/ui/SideSheet";
import { Button } from "@/components/ui/primitives";
import { useAuditTemplates } from "@/hooks/use-audit-templates";
import { useAuditTags } from "@/hooks/use-audit-tags";
import { useCreateAudit } from "@/hooks/use-create-audit";
import { useApiMutation } from "@/packages/features-shared";
import { api } from "convex/_generated/api";
import { WizardStepIndicator } from "./WizardStepIndicator";
import { Step1DetailsForm } from "./Step1DetailsForm";
import { Step2Generating } from "./Step2Generating";
import { Step3ReviewChecklist } from "./Step3ReviewChecklist";
import { getDefaultFormValues } from "./wizardData";
import type { Id } from "@/packages/types";
import type { UIAuditCategory, UIChecklistItem } from "@/packages/types";
import type { WizardStep, NewAuditForm, ChecklistSource } from "./types";

// ─── Upload File State ──────────────────────────────────────────────────────
interface UploadedFileState {
  file: File;
  storageId: Id<"_storage"> | null; // null while uploading
}

// ─── Session Storage Persistence ───────────────────────────────────────────
const FORM_STORAGE_KEY = "new-audit-wizard-form";

function loadPersistedForm(): NewAuditForm {
  const defaults = getDefaultFormValues();
  try {
    const raw = sessionStorage.getItem(FORM_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<NewAuditForm>;
      return {
        ...defaults,
        ...parsed,
        checklistSource: parsed.checklistSource ?? null,
      };
    }
  } catch {
    // Ignore parse errors — fall back to defaults
  }
  return defaults;
}

function persistForm(form: NewAuditForm): void {
  try {
    sessionStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(form));
  } catch {
    // Ignore storage errors (e.g. quota exceeded)
  }
}

function clearPersistedForm(): void {
  try {
    sessionStorage.removeItem(FORM_STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
}

interface NewAuditWizardProps {
  open: boolean;
  onClose: () => void;
}

const STEP_TITLES: Record<WizardStep, string> = {
  details: "New Audit",
  generating: "Generating Checklist",
  review: "Review Checklist",
};

const STEP_INDEX: Record<WizardStep, number> = {
  details: 0,
  generating: 1,
  review: 2,
};

const stepVariants = {
  enter: (dir: number) => ({
    opacity: 0,
    x: dir * 40,
    scale: 0.98,
  }),
  center: {
    opacity: 1,
    x: 0,
    scale: 1,
  },
  exit: (dir: number) => ({
    opacity: 0,
    x: dir * -40,
    scale: 0.98,
  }),
};

export function NewAuditWizard({ open, onClose }: NewAuditWizardProps) {
  const navigate = useNavigate();
  const { workspaceSlug } = useParams({ strict: false });
  const { templates } = useAuditTemplates();

  const { tags } = useAuditTags();
  const { createAudit, isCreating } = useCreateAudit();

  const [step, setStep] = useState<WizardStep>("details");
  const [form, setForm] = useState<NewAuditForm>(loadPersistedForm);
  const [auditId, setAuditId] = useState<Id<"audits"> | null>(null);
  const [categories, setCategories] = useState<UIAuditCategory[]>([]);
  const [confirming, setConfirming] = useState(false);
  const [direction, setDirection] = useState(1);
  const lastDeletedRef = useRef<{
    item: UIChecklistItem;
    categoryId: string;
    index: number;
  } | null>(null);

  // File upload state
  const [uploadedFile, setUploadedFile] = useState<UploadedFileState | null>(
    null,
  );
  const [isUploading, setIsUploading] = useState(false);
  const generateUploadUrlMutation = useApiMutation(
    api.admin_standards_mutations.generateUploadUrl,
    {
      name: "generate-upload-url",
      silentSuccess: true,
    },
  );

  // Persist form values to sessionStorage whenever they change
  useEffect(() => {
    persistForm(form);
  }, [form]);

  // Reset checklistSource and uploadedFile when templateId changes
  const prevTemplateIdRef = useRef(form.templateId);
  useEffect(() => {
    if (form.templateId !== prevTemplateIdRef.current) {
      prevTemplateIdRef.current = form.templateId;
      setForm((prev) => ({ ...prev, checklistSource: null }));
      setUploadedFile(null);
      setIsUploading(false);
    }
  }, [form.templateId]);

  const handleChecklistSourceChange = useCallback((source: ChecklistSource) => {
    setForm((prev) => ({ ...prev, checklistSource: source }));
  }, []);

  const handleFileSelected = useCallback(
    async (file: File) => {
      setUploadedFile({ file, storageId: null });
      setIsUploading(true);
      try {
        const uploadUrl = await generateUploadUrlMutation.mutateAsync({});
        const result = await fetch(uploadUrl, {
          method: "POST",
          headers: { "Content-Type": file.type },
          body: file,
        });
        const { storageId } = (await result.json()) as {
          storageId: Id<"_storage">;
        };
        setUploadedFile({ file, storageId });
      } catch {
        toast.error("File upload failed. Please try again.");
        setUploadedFile(null);
      } finally {
        setIsUploading(false);
      }
    },
    [generateUploadUrlMutation],
  );

  const handleFileRemoved = useCallback(() => {
    setUploadedFile(null);
    setIsUploading(false);
  }, []);

  const handleUpdateChecklistItem = useCallback(
    (itemId: string, updates: Partial<UIChecklistItem>) => {
      setCategories((prev) =>
        prev.map((cat) => ({
          ...cat,
          items: cat.items.map((item) =>
            item.id === itemId ? { ...item, ...updates } : item,
          ),
        })),
      );
    },
    [],
  );

  const handleUndoDelete = useCallback(() => {
    const deleted = lastDeletedRef.current;
    if (!deleted) return;
    setCategories((prev) =>
      prev.map((cat) => {
        if (cat.id !== deleted.categoryId) return cat;
        const items = [...cat.items];
        items.splice(deleted.index, 0, deleted.item);
        return { ...cat, items };
      }),
    );
    lastDeletedRef.current = null;
  }, []);

  const handleDeleteChecklistItem = useCallback(
    (itemId: string) => {
      setCategories((prev) => {
        const newCategories = prev.map((cat) => {
          const idx = cat.items.findIndex((i) => i.id === itemId);
          if (idx === -1) return cat;
          lastDeletedRef.current = {
            item: cat.items[idx],
            categoryId: cat.id,
            index: idx,
          };
          return {
            ...cat,
            items: cat.items.filter((i) => i.id !== itemId),
          };
        });
        return newCategories;
      });
      toast("Checklist item removed", {
        action: { label: "Undo", onClick: handleUndoDelete },
      });
    },
    [handleUndoDelete],
  );

  const resetWizard = useCallback(() => {
    setStep("details");
    setForm(getDefaultFormValues());
    setAuditId(null);
    setCategories([]);
    setConfirming(false);
    setDirection(1);
    setUploadedFile(null);
    setIsUploading(false);
    clearPersistedForm();
  }, []);

  const handleClose = useCallback(() => {
    // If past the details step (audit already created in backend), fully reset
    if (step !== "details") {
      resetWizard();
    }
    // If on details step, do NOT reset — let form values persist for reopen
    onClose();
  }, [onClose, step, resetWizard]);

  const goToStep = useCallback(
    (next: WizardStep) => {
      setDirection(STEP_INDEX[next] > STEP_INDEX[step] ? 1 : -1);
      setStep(next);
    },
    [step],
  );

  const handleGenerate = useCallback(async () => {
    const selectedTemplate = (templates ?? []).find(
      (t) => t.id === form.templateId,
    );
    if (!selectedTemplate) return;

    // For "upload", require storageId to be ready
    if (form.checklistSource === "upload" && !uploadedFile?.storageId) {
      toast.error("Please wait for the file upload to complete.");
      return;
    }

    try {
      const newAuditId = await createAudit({
        name: form.displayName,
        templateCode: selectedTemplate.code,
        templateId: selectedTemplate.id,
        description: form.description || undefined,
        deadline: form.deadline ? new Date(form.deadline).getTime() : undefined,
        trigger: form.trigger,
        tagIds:
          form.tagIds.length > 0
            ? (form.tagIds as Id<"auditTags">[])
            : undefined,
        notes: form.notes || undefined,
        auditorRequestText: form.auditorRequestText || undefined,
        checklistSource: form.checklistSource ?? "ai_research",
        uploadedDocumentStorageId: uploadedFile?.storageId ?? undefined,
      });
      setAuditId(newAuditId);

      // For all workflows, go to "generating" step.
      // "official" sets checklistGenerationStatus to "ready" synchronously,
      // so Step2Generating will immediately auto-advance to Step 3.
      goToStep("generating");
    } catch (_error) {
      toast.error("Failed to create audit. Please try again.");
    }
  }, [form, templates, createAudit, goToStep, uploadedFile]);

  const handleChecklistReady = useCallback(
    (readyCategories: UIAuditCategory[]) => {
      setCategories(readyCategories);
      goToStep("review");
    },
    [goToStep],
  );

  const handleConfirm = useCallback(() => {
    if (!auditId) return;
    setConfirming(true);
    setTimeout(() => {
      navigate({
        to: "/$workspaceSlug/audits/$auditId",
        params: {
          workspaceSlug: workspaceSlug ?? "",
          auditId: auditId as string,
        },
      });
      // On successful creation, reset wizard and clear persisted form
      resetWizard();
      onClose();
    }, 600);
  }, [auditId, navigate, resetWizard, onClose, workspaceSlug]);

  const handleBack = useCallback(() => {
    goToStep("details");
  }, [goToStep]);

  const isUploadReady =
    form.checklistSource !== "upload" || uploadedFile?.storageId != null;

  const isFormValid =
    form.templateId !== "" &&
    form.displayName !== "" &&
    form.trigger !== "" &&
    form.deadline !== "" &&
    form.checklistSource !== null &&
    isUploadReady;

  return (
    <SideSheet open={open} onClose={handleClose}>
      <SideSheet.Header
        title={STEP_TITLES[step]}
        icon={<ClipboardList size={20} />}
      />

      <SideSheet.Body>
        <WizardStepIndicator currentStep={step} />

        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={step}
            custom={direction}
            variants={stepVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={springs.smooth}
          >
            {step === "details" && (
              <Step1DetailsForm
                form={form}
                onChange={setForm}
                onChecklistSourceChange={handleChecklistSourceChange}
                templates={templates ?? []}
                tags={tags ?? []}
                uploadedFile={uploadedFile}
                isUploading={isUploading}
                onFileSelected={handleFileSelected}
                onFileRemoved={handleFileRemoved}
              />
            )}
            {step === "generating" && auditId && (
              <Step2Generating
                auditId={auditId}
                auditName={form.displayName}
                onChecklistReady={handleChecklistReady}
                onCancel={handleClose}
              />
            )}
            {step === "review" && categories.length > 0 && (
              <Step3ReviewChecklist
                categories={categories}
                auditName={form.displayName}
                onUpdateItem={handleUpdateChecklistItem}
                onDeleteItem={handleDeleteChecklistItem}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </SideSheet.Body>

      <SideSheet.Footer>
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            className="flex w-full items-center justify-end gap-3"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={springs.snappy}
          >
            {step === "details" && (
              <>
                <Button variant="secondary" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleGenerate}
                  disabled={!isFormValid || isUploading}
                  loading={isCreating}
                >
                  {form.checklistSource === "official"
                    ? "Next: Review Checklist"
                    : "Next: Generate Checklist"}
                </Button>
              </>
            )}
            {step === "generating" && (
              <>
                <Button variant="secondary" onClick={handleClose}>
                  Cancel
                </Button>
                <Button variant="primary" loading disabled>
                  Working...
                </Button>
              </>
            )}
            {step === "review" && (
              <>
                <Button variant="secondary" onClick={handleBack}>
                  Back to Details
                </Button>
                <Button
                  variant="primary"
                  onClick={handleConfirm}
                  loading={confirming}
                >
                  Confirm &amp; Open Audit
                </Button>
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </SideSheet.Footer>
    </SideSheet>
  );
}
